import dayjs from '~/timeInit';

const DAYS_PER_YEAR = 365;
const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;
const MILLISECONDS_PER_MINUTE = 1000 * 60;
const MILLISECONDS_PER_HOUR = MILLISECONDS_PER_MINUTE * MINUTES_PER_HOUR;
const MILLISECONDS_PER_DAY = MILLISECONDS_PER_HOUR * HOURS_PER_DAY;

/**
 * Formats the time until a voucher-user assignment expires.
 *
 * This uses the same boundary as svc-promo's eligibility query: an assignment
 * is no longer active once `expired_at <= NOW()`.
 */
/** Formats the live countdown shown in the voucher-user table. */
export function formatVoucherUserTimeRemaining(expiredAt: string | undefined, now = Date.now()): string {
  if (!expiredAt) {
    return 'No expiry';
  }

  const expiresAt = dayjs(expiredAt);
  if (!expiresAt.isValid()) {
    return 'Invalid expiry';
  }

  const remainingMs = expiresAt.diff(now);
  if (remainingMs <= 0) {
    return 'Expired';
  }

  const duration = dayjs.duration(remainingMs);
  const days = Math.floor(duration.asDays());
  if (days > DAYS_PER_YEAR) {
    const years = Math.floor(duration.asYears());
    return `${years} ${years === 1 ? 'year' : 'years'}`;
  }

  // Day.js decomposes duration units relative to months. The day count above
  // is a total, so derive its smaller units from the same total remainder.
  const hours = Math.floor((remainingMs % MILLISECONDS_PER_DAY) / MILLISECONDS_PER_HOUR);
  const minutes = Math.floor((remainingMs % MILLISECONDS_PER_HOUR) / MILLISECONDS_PER_MINUTE);
  const dayLabel = days === 1 ? 'day' : 'days';
  const hourLabel = hours === 1 ? 'hour' : 'hours';
  const minuteLabel = minutes === 1 ? 'minute' : 'minutes';

  return `${days} ${dayLabel} ${hours} ${hourLabel} ${minutes} ${minuteLabel}`;
}
